import { Box, Paper, Skeleton, Stack, Typography } from '@mui/material'
import { blue, green, grey, lightGreen, orange } from '@mui/material/colors'
import { StatusFlowDto, StatusFlowPhaseDto } from '../../../interfaces/IDashboard'
import { usePolling } from '../../../hooks/usePolling'
import InfoTooltip from './InfoTooltip'

interface StatusFlowCardProps {
  info?: string
}

const HISTORICAL_POLL_INTERVAL_MS = 5 * 60_000
const FALLBACK_BAR_COLOR = grey[500]
const LABEL_MIN_WIDTH = 130
const VALUE_MIN_WIDTH = 60
const SAMPLE_MIN_WIDTH = 34
const MIN_BAR_PCT = 4
const CARD_MIN_HEIGHT = 240

// Maps backend EventStatus enum keys (as stored in EventChange.PreviousValueKey) to the same
// Norwegian labels and chip colors used elsewhere in the app (EnumToString.EventStatusDictionary
// and components/Chip/EventChip.tsx). Keep in sync if either source changes.
const STATUS_STYLE: Record<string, { label: string; color: string }> = {
  Open: { label: 'Åpen', color: blue[200] },
  Reported: { label: 'Rapportert', color: blue[200] },
  Requested: { label: 'Forespurt', color: blue[200] },
  Investigating: { label: 'Utredning', color: orange[300] },
  FixInProgress: { label: 'Feilretting pågår', color: orange[300] },
  AwaitingResponse: { label: 'Venter svar', color: orange[300] },
  Evaluation: { label: 'Evaluering', color: green[300] },
  Approved: { label: 'Godkjent', color: green[300] },
  FollowUp: { label: 'Oppfølging', color: green[300] },
  ProvisionallyResolved: { label: 'Provisorisk løst', color: lightGreen[300] },
  Closed: { label: 'Lukket', color: grey[300] },
}

function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '–'
  if (minutes < 60) return `${Math.round(minutes)} min`
  if (minutes < 60 * 24) return `${(minutes / 60).toFixed(1)} t`
  return `${(minutes / (60 * 24)).toFixed(1)} d`
}

function maxMedian(phases: StatusFlowPhaseDto[] | undefined): number {
  if (!phases) return 0
  let max = 0
  for (const p of phases) {
    if (p.medianMinutes !== null && p.medianMinutes !== undefined && p.medianMinutes > max) {
      max = p.medianMinutes
    }
  }
  return max
}

interface PhaseRowProps {
  phase: StatusFlowPhaseDto
  maxMinutes: number
}

function PhaseRow({ phase, maxMinutes }: PhaseRowProps) {
  const widthPct =
    phase.medianMinutes !== null && phase.medianMinutes !== undefined && maxMinutes > 0
      ? Math.max(MIN_BAR_PCT, (phase.medianMinutes / maxMinutes) * 100)
      : 0
  const style = STATUS_STYLE[phase.status]
  const displayLabel = style?.label ?? phase.status
  const barColor = style?.color ?? FALLBACK_BAR_COLOR
  return (
    <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
      <Typography variant="body2" sx={{ minWidth: LABEL_MIN_WIDTH }}>
        {displayLabel}
      </Typography>
      <Box sx={{ flex: 1, height: 6, backgroundColor: 'action.hover', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ width: `${widthPct}%`, height: '100%', backgroundColor: barColor, borderRadius: 3 }} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: SAMPLE_MIN_WIDTH, textAlign: 'right' }}>
        n={phase.sample}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: VALUE_MIN_WIDTH, textAlign: 'right' }}>
        {formatMinutes(phase.medianMinutes)}
      </Typography>
    </Stack>
  )
}

function StatusFlowCard({ info }: StatusFlowCardProps) {
  const { data } = usePolling<StatusFlowDto>('/api/stats/dashboard/status-flow?window=30d', HISTORICAL_POLL_INTERVAL_MS)
  const loaded = data !== null
  const max = maxMedian(data?.phases)

  let body
  if (!loaded) {
    body = (
      <Stack spacing={1}>
        <Skeleton variant="rounded" height={14} />
        <Skeleton variant="rounded" height={14} />
        <Skeleton variant="rounded" height={14} />
      </Stack>
    )
  } else if (!data || data.phases.length === 0) {
    body = (
      <Typography variant="body2" color="text.secondary">
        Ingen Status-overganger i vinduet.
      </Typography>
    )
  } else {
    body = (
      <Stack spacing={0.75}>
        {data.phases.map((p) => (
          <PhaseRow key={p.status} phase={p} maxMinutes={max} />
        ))}
      </Stack>
    )
  }

  return (
    <Paper
      elevation={1}
      sx={{ p: 2, height: '100%', minHeight: CARD_MIN_HEIGHT, display: 'flex', flexDirection: 'column' }}
    >
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Tid per status-fase (30d)
        </Typography>
        {info && <InfoTooltip text={info} />}
      </Stack>
      {body}
    </Paper>
  )
}

export default StatusFlowCard
